// MEDICATION ORDERS and MEDICATION ADMINISTRATION — rendered as two
// visually distinct things that happen to share a row.
//
// ── The rule this file enforces at the last mile ─────────────────
//
// An order is an intention; an administration is an event. The schema keeps
// them in separate tables, the routes refuse to infer one from the other, and
// here the UI must not undo that by drawing them as one status. So every dose
// cell shows the SLOT (what the order says should happen) and, separately,
// what was CHARTED against it. A slot with nothing charted reads "Nothing
// charted" — never "not given", which is a clinical claim nobody made.
//
// Recording a dose is the one action in this workspace behind a modal, on
// purpose: it is where a mis-tap has a patient-safety consequence.
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input, NativeSelect } from '@/components/ui/Input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/Dialog';
import VoiceFormFiller, { type VoiceField } from '@/components/VoiceFormFiller';
import { LoadingState } from '@/components/ui/States';
import { ApiError } from '@/lib/api/client';
import { appendText, numField, selectField, textField, vBool, vNum, vOneOf, vText, yesNoField, type Extracted } from '@/lib/voiceFill';
import { useMar, useMedicationOrders, useCreateMedicationOrder, useRecordAdministration,
  useHoldOrder, useResumeOrder, useDiscontinueOrder, useDispenseOrder } from '../hooks';
import { searchMedicines } from '../api';
import type { AdministrationStatus, MarDose, MedicationOrder, MedicineSearchResult } from '../types';
import { Section, Empty, DoseStateBadge, OrderStatusBadge, fmtTime, fmtDateTime, useCan, BackdateField } from '../ui';
import { useInventory } from '@/features/pharmacy/useInventory';
import { useDispensingSettings } from '@/features/pharmacy/useNearExpiry';
import { pickFefoBatch, isNearExpiry } from '@/features/pharmacy/batchSelection';
import { WardRequestsPanel } from './WardRequestsPanel';
import { TextPromptDialog } from '@/components/ui/TextPromptDialog';

const FREQUENCIES: Record<string, string[]> = {
  OD: ['09:00'],
  BD: ['09:00', '21:00'],
  TDS: ['08:00', '14:00', '20:00'],
  QID: ['06:00', '12:00', '18:00', '00:00'],
  HS: ['21:00'],
  STAT: [],
};

const ROUTES = ['PO', 'IV', 'IM', 'SC', 'Topical', 'Inhaled', 'PR', 'SL'] as const;

// "Ceftriaxone 1 gram IV twice daily for pneumonia" in one breath. The drug
// name only fills the search box — the doctor still picks the catalog match
// (the dropdown opens on its own), exactly as when typed.
const ORDER_VOICE_FIELDS: VoiceField[] = [
  textField('drug_name', 'Drug name (generic or brand), without the strength'),
  textField('strength', 'Strength of the product, e.g. 500 mg, 1 g'),
  numField('dose_quantity', 'Dose quantity per administration (number only)'),
  textField('dose_unit', 'Dose unit — mg, g, mcg, mL, tablet, unit'),
  selectField('route', 'Route — PO (oral), IV, IM, SC, Topical, Inhaled, PR, SL (sublingual)', ROUTES),
  selectField('frequency_code', 'Frequency — OD (once daily), BD (twice), TDS (three times), QID (four times), HS (at bedtime), STAT (once now)',
    Object.keys(FREQUENCIES)),
  yesNoField('is_prn', 'PRN / as needed / SOS'),
  textField('indication', 'Indication — what it is for'),
];

const ADMIN_VOICE_FIELDS: VoiceField[] = [
  { key: 'reason', label: 'Reason', type: 'text' },
  { key: 'remarks', label: 'Remarks', type: 'text' },
];

// ── Record an administration ─────────────────────────────────────

function AdministerDialog({ open, onClose, order, dose, admissionId }: {
  open: boolean; onClose: () => void;
  order: MedicationOrder; dose: MarDose | null; admissionId: number;
}) {
  const record = useRecordAdministration(admissionId);
  const [status, setStatus] = useState<AdministrationStatus>('given');
  const [reason, setReason] = useState('');
  const [doseQty, setDoseQty] = useState(order.dose_quantity != null ? String(order.dose_quantity) : '');
  const [remarks, setRemarks] = useState('');
  const [administeredAt, setAdministeredAt] = useState<string | undefined>(undefined);
  const [err, setErr] = useState<string | null>(null);

  // The server enforces this too (422 without it). Mirrored here so the
  // clinician is told before the round-trip, not after.
  const needsReason = status !== 'given';

  const submit = () => {
    setErr(null);
    record.mutate({
      orderId: order.id,
      input: {
        status: status as Exclude<AdministrationStatus, 'cancelled'>,
        scheduled_datetime: dose?.scheduled_datetime ?? null,
        administered_datetime: status === 'given' ? (administeredAt ?? new Date().toISOString()) : null,
        dose_quantity: doseQty ? Number(doseQty) : null,
        dose_unit: order.dose_unit ?? undefined,
        route: order.route ?? undefined,
        not_given_reason: needsReason ? reason : undefined,
        remarks: remarks || undefined,
      },
    }, {
      onSuccess: () => { onClose(); setReason(''); setRemarks(''); setStatus('given'); },
      onError: (e) => setErr(e instanceof ApiError ? e.message : 'Could not record this dose.'),
    });
  };

  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Record dose — {order.drug_name}</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="rounded-lg bg-muted px-3 py-2 text-[12.5px]">
            <div className="font-semibold">
              {order.drug_name}{order.strength ? ` ${order.strength}` : ''}
            </div>
            <div className="text-ink-muted">
              {[order.dose_quantity && `${order.dose_quantity} ${order.dose_unit ?? ''}`.trim(),
                order.route, order.frequency_code].filter(Boolean).join(' · ')}
            </div>
            {dose?.scheduled_datetime && (
              <div className="mt-1 text-ink-muted">Scheduled for {fmtDateTime(dose.scheduled_datetime)}</div>
            )}
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold">Outcome</label>
            <NativeSelect value={status} onChange={e => setStatus(e.target.value as AdministrationStatus)}>
              <option value="given">Given</option>
              <option value="not_given">Not given</option>
              <option value="refused">Refused by patient</option>
              <option value="held">Held</option>
            </NativeSelect>
          </div>

          {status === 'given' && (
            <>
              <div>
                <label className="mb-1 block text-xs font-semibold">
                  Dose given {order.dose_unit ? `(${order.dose_unit})` : ''}
                </label>
                <Input value={doseQty} onChange={e => setDoseQty(e.target.value)} inputMode="decimal" />
              </div>
              <BackdateField value={administeredAt} onChange={setAdministeredAt} label="Given at" />
            </>
          )}

          <VoiceFormFiller
            fields={needsReason ? ADMIN_VOICE_FIELDS : ADMIN_VOICE_FIELDS.filter(f => f.key === 'remarks')}
            label="🎙 Dictate reason / remarks"
            onFields={extracted => {
              if (extracted.reason) setReason(extracted.reason);
              if (extracted.remarks) setRemarks(extracted.remarks);
            }}
          />

          {needsReason && (
            <div>
              <label className="mb-1 block text-xs font-semibold">
                Reason <span className="font-normal text-ink-muted">(required)</span>
              </label>
              <Input
                value={reason}
                onChange={e => setReason(e.target.value)}
                placeholder="e.g. patient nil by mouth, nausea, awaiting review"
              />
              <p className="mt-1 text-[11px] text-ink-muted">
                An omitted dose without a reason is not a usable record.
              </p>
            </div>
          )}

          <div>
            <label className="mb-1 block text-xs font-semibold">Remarks (optional)</label>
            <Input value={remarks} onChange={e => setRemarks(e.target.value)} />
          </div>

          {err && <div className="rounded bg-danger-soft px-3 py-2 text-[12px] text-danger-ink">{err}</div>}

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button
              onClick={submit}
              loading={record.isPending}
              disabled={needsReason && !reason.trim()}
            >
              Record dose
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Dispense against an order ───────────────────────────────────
//
// Deliberately simpler than the full Dispensing/counter cart: an order names
// exactly one drug, so the batch is auto-picked earliest-expiry-first
// (pickFefoBatch) rather than letting the pharmacist choose. If the earliest
// batch can't cover the whole quantity on its own, this dialog asks for a
// smaller quantity rather than splitting across batches — an attender buying
// a large/mixed-batch quantity belongs on the IP Pharmacy counter screen,
// which supports that.
function DispenseOrderDialog({ open, onClose, order, admissionId }: {
  open: boolean; onClose: () => void; order: MedicationOrder; admissionId: number;
}) {
  const dispense = useDispenseOrder(admissionId);
  const inventoryQuery = useInventory({ channel: 'ip_order' });
  const { near_expiry_days: nearExpiryDays } = useDispensingSettings();
  const [medicineId, setMedicineId] = useState<number | null>(order.medicine_id);
  const [drugResults, setDrugResults] = useState<MedicineSearchResult[]>([]);
  const [searchText, setSearchText] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [prescriberName, setPrescriberName] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [flag, setFlag] = useState<{ implied_quantity: number | null; total_after: number; exceeds: boolean } | null>(null);

  // An order with no catalog link (free-text drug_name) can't be sold out of
  // inventory until the pharmacist resolves which stocked product it is —
  // mirrors the same optional resolve-as-you-type NewOrderForm already uses.
  useEffect(() => {
    if (medicineId || searchText.trim().length < 2) { setDrugResults([]); return; }
    const t = setTimeout(() => { searchMedicines(searchText).then(setDrugResults).catch(() => setDrugResults([])); }, 250);
    return () => clearTimeout(t);
  }, [searchText, medicineId]);

  const inventory = inventoryQuery.data ?? [];
  const dispensable = inventory.filter(m => m.medicine_id === medicineId && m.quantity_available > 0 && new Date(m.expiry_date) >= new Date());
  const qtyNum = Number(quantity) || 0;
  const batch = qtyNum > 0 ? pickFefoBatch(dispensable, qtyNum) : null;
  const shortBatch = !!(medicineId && qtyNum > 0 && (!batch || batch.quantity_available < qtyNum));

  const submit = () => {
    setErr(null);
    if (!medicineId || !batch) { setErr('Resolve a stocked product for this drug first.'); return; }
    dispense.mutate({
      orderId: order.id,
      input: {
        items: [{ inventory_id: batch.id, quantity: qtyNum }],
        prescriber_name: prescriberName || undefined,
      },
    }, {
      onSuccess: (result) => { setFlag(result.quantity_flag); },
      onError: (e) => setErr(e instanceof ApiError ? e.message : 'Could not dispense this order.'),
    });
  };

  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Dispense — {order.drug_name}{order.strength ? ` ${order.strength}` : ''}</DialogTitle>
        </DialogHeader>

        {flag ? (
          <div className="space-y-3">
            <div className="rounded bg-success-soft px-3 py-2 text-[12.5px] text-success-ink">
              Bill created. {flag.exceeds && flag.implied_quantity != null && (
                <div className="mt-1 text-warn-ink">
                  Note: {flag.total_after} dispensed so far exceeds the ~{flag.implied_quantity} the order's schedule implies — advisory only, nothing was blocked.
                </div>
              )}
            </div>
            <div className="flex justify-end"><Button onClick={onClose}>Done</Button></div>
          </div>
        ) : (
          <div className="space-y-3">
            {!medicineId ? (
              <div className="relative">
                <label className="mb-1 block text-xs font-semibold">
                  Which stocked product is this? <span className="font-normal text-ink-muted">(order has no catalog link)</span>
                </label>
                <Input value={searchText} onChange={e => setSearchText(e.target.value)} placeholder={order.drug_name} />
                {drugResults.length > 0 && (
                  <div className="absolute z-10 mt-1 max-h-32 w-full overflow-y-auto rounded-[var(--radius)] border border-line bg-surface shadow-md">
                    {drugResults.map(r => (
                      <button key={r.id} type="button" className="block w-full px-2 py-1 text-left text-xs hover:bg-canvas"
                        onClick={() => { setMedicineId(r.id); setSearchText(`${r.generic_name}${r.brand_name ? ` — ${r.brand_name}` : ''}`); setDrugResults([]); }}>
                        {r.generic_name}{r.brand_name ? ` — ${r.brand_name}` : ''}{r.strength ? ` ${r.strength}` : ''}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <>
                <div>
                  <label className="mb-1 block text-xs font-semibold">Quantity</label>
                  <Input value={quantity} onChange={e => setQuantity(e.target.value)} inputMode="numeric" />
                </div>
                {batch && (
                  <div className="rounded-lg bg-muted px-3 py-2 text-[12px]">
                    Batch {batch.batch_number} — exp {new Date(batch.expiry_date).toLocaleDateString('en-IN')}, {batch.quantity_available} available
                    {isNearExpiry(batch.expiry_date, nearExpiryDays) && <Badge variant="warn" className="ml-2">⏳ Near expiry</Badge>}
                  </div>
                )}
                {shortBatch && (
                  <div className="rounded bg-warn-soft px-3 py-2 text-[12px] text-warn-ink">
                    No single batch covers {qtyNum} units. Reduce the quantity, or use the IP Pharmacy counter screen to split across batches.
                  </div>
                )}
                <div>
                  <label className="mb-1 block text-xs font-semibold">
                    Prescriber name <span className="font-normal text-ink-muted">(only needed for Schedule H1/X)</span>
                  </label>
                  <Input value={prescriberName} onChange={e => setPrescriberName(e.target.value)} placeholder={order.prescriber_name || ''} />
                </div>
              </>
            )}

            {err && <div className="rounded bg-danger-soft px-3 py-2 text-[12px] text-danger-ink">{err}</div>}

            <div className="flex justify-end gap-2 pt-1">
              <Button variant="ghost" onClick={onClose}>Cancel</Button>
              <Button onClick={submit} loading={dispense.isPending} disabled={!medicineId || shortBatch || qtyNum <= 0}>
                Dispense
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── New order ────────────────────────────────────────────────────

function NewOrderForm({ admissionId, onDone }: { admissionId: number; onDone: () => void }) {
  const create = useCreateMedicationOrder(admissionId);
  const [f, setF] = useState({
    drug_name: '', strength: '', dose_quantity: '', dose_unit: '', route: 'PO',
    frequency_code: 'BD', indication: '', is_prn: false, instructions: '',
  });
  const [medicineId, setMedicineId] = useState<number | null>(null);
  const [drugResults, setDrugResults] = useState<MedicineSearchResult[]>([]);
  const [times, setTimes] = useState<string[]>(FREQUENCIES.BD!);
  const [startDatetime, setStartDatetime] = useState<string | undefined>(undefined);
  const [err, setErr] = useState<string | null>(null);
  const set = (k: string, v: string | boolean) => setF(p => ({ ...p, [k]: v }));

  // Optional resolve-as-you-type against the medicines master (§23 of the
  // Drug Master architecture doc) — picking a result additionally sets
  // medicine_id; typing freely without picking one leaves it null, which
  // stays fully supported (IP ordering must never require a catalog match).
  useEffect(() => {
    if (medicineId || f.drug_name.trim().length < 2) { setDrugResults([]); return; }
    const t = setTimeout(() => { searchMedicines(f.drug_name).then(setDrugResults).catch(() => setDrugResults([])); }, 250);
    return () => clearTimeout(t);
  }, [f.drug_name, medicineId]);

  const pickDrug = (r: MedicineSearchResult) => {
    setMedicineId(r.id);
    setF(p => ({ ...p, drug_name: `${r.generic_name}${r.brand_name ? ` — ${r.brand_name}` : ''}`, strength: r.strength || p.strength }));
    setDrugResults([]);
  };

  const pickFrequency = (code: string) => {
    set('frequency_code', code);
    setTimes(FREQUENCIES[code] ?? []);
  };

  const applyVoice = (x: Extracted) => {
    const drug = vText(x, 'drug_name');
    if (drug) setMedicineId(null);
    const freq = vOneOf(x, 'frequency_code', Object.keys(FREQUENCIES));
    if (freq) pickFrequency(freq);
    const dose = vNum(x, 'dose_quantity');
    const prn = vBool(x, 'is_prn');
    setF(p => ({
      ...p,
      ...(drug ? { drug_name: drug } : {}),
      ...(vText(x, 'strength') ? { strength: vText(x, 'strength')! } : {}),
      ...(dose !== undefined && dose > 0 ? { dose_quantity: String(dose) } : {}),
      ...(vText(x, 'dose_unit') ? { dose_unit: vText(x, 'dose_unit')! } : {}),
      ...(vOneOf(x, 'route', ROUTES) ? { route: vOneOf(x, 'route', ROUTES)! } : {}),
      ...(prn !== undefined ? { is_prn: prn } : {}),
      ...(vText(x, 'indication') ? { indication: appendText(p.indication, vText(x, 'indication')) } : {}),
    }));
  };

  const submit = () => {
    setErr(null);
    create.mutate({
      drug_name: f.drug_name.trim(),
      medicine_id: medicineId,
      strength: f.strength || undefined,
      dose_quantity: f.dose_quantity ? Number(f.dose_quantity) : null,
      dose_unit: f.dose_unit || undefined,
      route: f.route || undefined,
      frequency_code: f.frequency_code || undefined,
      administration_times: times,
      is_prn: f.is_prn,
      indication: f.indication || undefined,
      instructions: f.instructions || undefined,
      start_datetime: startDatetime,
      // A scheduled order with no times is refused server-side unless this
      // says the order genuinely has no schedule.
      unscheduled: !f.is_prn && times.length === 0,
    }, {
      onSuccess: onDone,
      onError: (e) => setErr(e instanceof ApiError ? e.message : 'Could not create this order.'),
    });
  };

  return (
    <div className="mb-3 rounded-lg border border-border bg-muted/40 p-3">
      <VoiceFormFiller fields={ORDER_VOICE_FIELDS} onFields={applyVoice} label="🎙 Dictate order"
        hint="Ceftriaxone 1 gram IV twice daily for pneumonia" />
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        <div className="relative sm:col-span-2">
          <label className="mb-1 block text-xs font-semibold">Drug</label>
          <Input
            value={f.drug_name}
            onChange={e => { set('drug_name', e.target.value); setMedicineId(null); }}
            placeholder="e.g. Ceftriaxone"
            className={medicineId ? 'border-teal' : undefined}
            title={medicineId ? 'Resolved to an existing catalog product' : undefined}
          />
          {drugResults.length > 0 && (
            <div className="absolute z-10 mt-1 max-h-32 w-full overflow-y-auto rounded-[var(--radius)] border border-line bg-surface shadow-md">
              {drugResults.map(r => (
                <button key={r.id} type="button" className="block w-full px-2 py-1 text-left text-xs hover:bg-canvas" onClick={() => pickDrug(r)}>
                  {r.generic_name}{r.brand_name ? ` — ${r.brand_name}` : ''}{r.strength ? ` ${r.strength}` : ''}
                </button>
              ))}
            </div>
          )}
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold">Strength</label>
          <Input value={f.strength} onChange={e => set('strength', e.target.value)} placeholder="1 g" />
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold">Dose</label>
          <Input value={f.dose_quantity} onChange={e => set('dose_quantity', e.target.value)} inputMode="decimal" />
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold">Unit</label>
          <Input value={f.dose_unit} onChange={e => set('dose_unit', e.target.value)} placeholder="mg / g / mL" />
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold">Route</label>
          <NativeSelect value={f.route} onChange={e => set('route', e.target.value)}>
            {ROUTES.map(r => <option key={r}>{r}</option>)}
          </NativeSelect>
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold">Frequency</label>
          <NativeSelect value={f.frequency_code} onChange={e => pickFrequency(e.target.value)}>
            {Object.keys(FREQUENCIES).map(c => <option key={c}>{c}</option>)}
          </NativeSelect>
        </div>
        <div className="sm:col-span-2">
          <label className="mb-1 block text-xs font-semibold">
            Administration times
            <span className="ml-1 font-normal text-ink-muted">
              — when the ward should give it; drives the chart
            </span>
          </label>
          <Input
            value={times.join(', ')}
            onChange={e => setTimes(e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
            placeholder="09:00, 21:00"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="mb-1 block text-xs font-semibold">Indication</label>
          <Input value={f.indication} onChange={e => set('indication', e.target.value)} />
        </div>
        <label className="flex items-center gap-2 text-[13px]">
          <input type="checkbox" checked={f.is_prn} onChange={e => set('is_prn', e.target.checked)} />
          PRN (as needed)
        </label>
      </div>

      <div className="mt-2">
        <BackdateField value={startDatetime} onChange={setStartDatetime} label="Order started" />
      </div>

      {err && <div className="mt-2 rounded bg-danger-soft px-3 py-2 text-[12px] text-danger-ink">{err}</div>}

      <div className="mt-3 flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onDone}>Cancel</Button>
        <Button size="sm" onClick={submit} loading={create.isPending} disabled={!f.drug_name.trim()}>
          Place order
        </Button>
      </div>
    </div>
  );
}

// ── The chart ────────────────────────────────────────────────────

export function MedicationSection({ admissionId, readOnly }: { admissionId: number; readOnly: boolean }) {
  const can = useCan();
  const mar = useMar(admissionId);
  const orders = useMedicationOrders(admissionId);
  const hold = useHoldOrder(admissionId);
  const resume = useResumeOrder(admissionId);
  const discontinue = useDiscontinueOrder(admissionId);

  const [showNew, setShowNew] = useState(false);
  const [administering, setAdministering] = useState<{ order: MedicationOrder; dose: MarDose | null } | null>(null);
  const [dispensing, setDispensing] = useState<MedicationOrder | null>(null);
  // Hold / discontinue ask for a reason in a dialog — window.prompt() is
  // silently refused by embedded browsers (the desktop app's pane, Electron).
  const [stopping, setStopping] = useState<{ order: MedicationOrder; kind: 'hold' | 'discontinue' } | null>(null);
  const stopMutation = stopping?.kind === 'hold' ? hold : discontinue;

  const canOrder = can('ip:med-order') && !readOnly;
  const canAdminister = can('ip:med-admin') && !readOnly;
  // Dispensing is a pharmacy action wherever it happens — same module
  // Dispensing itself is gated on — not an ip:* grant, so a pharmacist
  // without ip:med-order can still fulfil an order from here.
  const canDispense = can('pharmacy') && !readOnly;

  if (mar.isLoading) return <LoadingState label="Loading medication chart…" />;

  const rows = mar.data?.orders ?? [];
  const stopped = (orders.data ?? []).filter(o => !['active', 'held'].includes(o.status));

  return (
    <div className="space-y-4">
      <Section
        focusId="medication_order medication_administration"
        title="Medication chart"
        subtitle="Each row shows what was ORDERED and, separately, what was ADMINISTERED. An order is never evidence that a dose was given."
        action={canOrder && !showNew && (
          <Button size="sm" data-focus="medication_order:new" onClick={() => setShowNew(true)}>+ Medication order</Button>
        )}
      >
        {showNew && <NewOrderForm admissionId={admissionId} onDone={() => setShowNew(false)} />}

        {!rows.length ? (
          <Empty label="No active medication orders" hint={canOrder ? 'Use “+ Medication order” to start one.' : undefined} />
        ) : (
          <div className="space-y-3">
            {rows.map(({ order, doses }) => (
              <div key={order.id} data-focus={`medication_order:${order.id}`} className="rounded-lg border border-border">
                {/* ── ORDER ── */}
                <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2 border-b border-border bg-muted/40 px-3 py-2">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-ink-muted">Order</span>
                      <span className="text-[13.5px] font-semibold">
                        {order.drug_name}{order.strength ? ` ${order.strength}` : ''}
                      </span>
                      <OrderStatusBadge status={order.status} />
                      {order.is_prn && <Badge variant="info">PRN</Badge>}
                    </div>
                    <div className="mt-0.5 text-[12px] text-ink-muted">
                      {[order.dose_quantity && `${order.dose_quantity} ${order.dose_unit ?? ''}`.trim(),
                        order.route, order.frequency_code,
                        order.administration_times?.length ? order.administration_times.join(', ') : null,
                      ].filter(Boolean).join(' · ')}
                    </div>
                    <div className="mt-0.5 text-[11px] text-ink-muted">
                      Started {fmtDateTime(order.start_datetime)}
                      {order.prescriber_name ? ` · ${order.prescriber_name}` : ''}
                      {order.indication ? ` · for ${order.indication}` : ''}
                    </div>
                    {order.status === 'held' && order.hold_reason && (
                      <div className="mt-1 text-[11.5px] text-warn-ink">On hold: {order.hold_reason}</div>
                    )}
                  </div>

                  {(canOrder || canDispense) && (
                    <div className="flex shrink-0 gap-1.5">
                      {canDispense && ['active', 'held'].includes(order.status) && (
                        <Button variant="primary" size="sm" onClick={() => setDispensing(order)}>💊 Dispense</Button>
                      )}
                      {canOrder && order.status === 'active' && (
                        <Button variant="ghost" size="sm" onClick={() => { hold.reset(); setStopping({ order, kind: 'hold' }); }}>Hold</Button>
                      )}
                      {canOrder && order.status === 'held' && (
                        <Button variant="ghost" size="sm" onClick={() => resume.mutate(order.id)}>Resume</Button>
                      )}
                      {canOrder && (
                        <Button variant="ghost" size="sm" onClick={() => { discontinue.reset(); setStopping({ order, kind: 'discontinue' }); }}>Discontinue</Button>
                      )}
                    </div>
                  )}
                </div>

                {/* ── ADMINISTRATION ── */}
                <div className="px-3 py-2">
                  <div className="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-ink-muted">
                    Administration
                  </div>
                  {!doses.length ? (
                    <div className="text-[12.5px] text-ink-muted">
                      {order.is_prn ? 'PRN — no scheduled doses. Nothing charted yet.' : 'No doses in this window.'}
                    </div>
                  ) : (
                    <div className="-mx-1 flex gap-1.5 overflow-x-auto px-1 pb-1">
                      {doses.map((d, i) => {
                        const charted = d.administration;
                        return (
                          <div key={i} className="min-w-[132px] shrink-0 rounded-md border border-border px-2 py-1.5">
                            <div className="text-[11.5px] font-semibold tabular-nums">
                              {d.scheduled_datetime ? fmtTime(d.scheduled_datetime) : 'Unscheduled'}
                            </div>
                            <div className="mt-1"><DoseStateBadge state={d.state} /></div>
                            {charted ? (
                              <div className="mt-1 text-[10.5px] text-ink-muted">
                                {charted.administered_datetime && <>at {fmtTime(charted.administered_datetime)}<br /></>}
                                {charted.administered_by_name || charted.administered_by || 'unknown'}
                                {charted.not_given_reason && (
                                  <div className="mt-0.5 italic">{charted.not_given_reason}</div>
                                )}
                              </div>
                            ) : canAdminister && d.state !== 'upcoming' ? (
                              <Button
                                variant="ghost" size="sm" className="mt-1 w-full"
                                onClick={() => setAdministering({ order, dose: d })}
                              >Record</Button>
                            ) : (
                              <div className="mt-1 text-[10.5px] text-ink-muted">Nothing charted</div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {canAdminister && (order.is_prn || !doses.length) && (
                    <Button variant="ghost" size="sm" className="mt-1.5"
                      onClick={() => setAdministering({ order, dose: null })}>
                      + Record a dose
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      <WardRequestsPanel admissionId={admissionId} orders={orders.data ?? []} readOnly={readOnly} />

      <TextPromptDialog
        open={!!stopping}
        title={stopping ? `${stopping.kind === 'hold' ? 'Hold' : 'Discontinue'} ${stopping.order.drug_name}${stopping.order.strength ? ` ${stopping.order.strength}` : ''}?` : ''}
        description={stopping?.kind === 'hold'
          ? 'No doses are due while the order is on hold. It can be resumed later.'
          : 'A discontinued order can no longer be given or supplied. This cannot be undone — write a new order to restart it.'}
        label={stopping?.kind === 'hold' ? 'Reason for holding' : 'Reason for discontinuing'}
        placeholder={stopping?.kind === 'hold' ? 'e.g. Nil by mouth before surgery' : 'e.g. Course complete'}
        confirmLabel={stopping?.kind === 'hold' ? 'Hold order' : 'Discontinue order'}
        danger={stopping?.kind === 'discontinue'}
        pending={stopMutation.isPending}
        error={stopMutation.error instanceof Error ? stopMutation.error.message : null}
        onConfirm={(reason) => {
          if (!stopping) return;
          stopMutation.mutate({ orderId: stopping.order.id, reason }, { onSuccess: () => setStopping(null) });
        }}
        onClose={() => setStopping(null)}
      />

      {stopped.length > 0 && (
        <Section title="Stopped orders" subtitle="Kept for the record — a discontinued order cannot be administered against.">
          <ul className="space-y-1.5">
            {stopped.map(o => (
              <li key={o.id} data-focus={`medication_order:${o.id}`} className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[12.5px]">
                <span className="font-medium">{o.drug_name}{o.strength ? ` ${o.strength}` : ''}</span>
                <OrderStatusBadge status={o.status} />
                {o.discontinue_reason && <span className="text-ink-muted">— {o.discontinue_reason}</span>}
                {o.discontinued_at && <span className="text-ink-muted">({fmtDateTime(o.discontinued_at)})</span>}
              </li>
            ))}
          </ul>
        </Section>
      )}

      {administering && (
        <AdministerDialog
          open
          onClose={() => setAdministering(null)}
          order={administering.order}
          dose={administering.dose}
          admissionId={admissionId}
        />
      )}

      {dispensing && (
        <DispenseOrderDialog
          open
          onClose={() => setDispensing(null)}
          order={dispensing}
          admissionId={admissionId}
        />
      )}
    </div>
  );
}
